import { AppSidebar } from "@/components/app-sidebar";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import {
  LoginGuard,
  RequireAuth,
  RequirePermission,
  RequireRole,
} from "@/lib/guards";
import AccountDeletion from "@/pages/account-deletion";
import AcceptInternalInvitation from "@/pages/accept-internal-invitations";
import AcceptInvitation from "@/pages/accept-invitation";
import AffiliateProfile from "@/pages/affiliate-profile";
import Affiliates from "@/pages/affiliates";
import AffiliatesReview from "@/pages/affiliates-review";
import AuditLogs from "@/pages/audit-logs";
import Categories from "@/pages/categories";
import CoachProfile from "@/pages/coach-profile";
import Coaches from "@/pages/coaches";
import ContentLibrary from "@/pages/content-library";
import ContentStudio from "@/pages/content-studio";
import Dashboard from "@/pages/dashboard";
import GdprCompliance from "@/pages/gdpr-compliance";
import HoroscopePage from "@/pages/horoscope";
import InternalUsers from "@/pages/internal-users";
import Login from "@/pages/login";
import NotFound from "@/pages/not-found";
import Notifications from "@/pages/notifications";
import PartnerRequestDetail from "@/pages/partner-request-detail";
import Profile from "@/pages/profile";
import Settings from "@/pages/settings";
import Subscriptions from "@/pages/subscriptions";
import SystemHealth from "@/pages/system-health";
import TagGroups from "@/pages/tag-groups";
import UserSupport from "@/pages/user-support";
import Users from "@/pages/users";
import { QueryClientProvider } from "@tanstack/react-query";
import { Route, Switch, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import ContentOperationsPage from "./pages/content-operations/content-operations-page";
import AffirmationCreatePage from "./pages/content/affirmation/affirmation-create";
import AffirmationEditPage from "./pages/content/affirmation/affirmation-edit";
import AffirmationListPage from "./pages/content/affirmation/affirmation-list";
import AudioCreatePage from "./pages/content/audio/audio-create";
import AudioEditPage from "./pages/content/audio/audio-edit";
import AudioListPage from "./pages/content/audio/audio-list";
import BlogCreatePage from "./pages/content/blog/blog-create";
import BlogEditPage from "./pages/content/blog/blog-edit";
import BlogListPage from "./pages/content/blog/blog-list";
import ChallengeCreatePage from "./pages/content/challenge/challenge-create";
import ChallengeEditPage from "./pages/content/challenge/challenge-edit";
import ChallengeListPage from "./pages/content/challenge/challenge-list";
import ActivityPage from "./pages/content/dashboard/activity-page";
import PublishedPage from "./pages/content/dashboard/published-page";
import QualityPage from "./pages/content/dashboard/quality-page";
import ReviewPage from "./pages/content/dashboard/review-page";
import JourneyCreatePage from "./pages/content/journey/journey-create";
import JourneyEditPage from "./pages/content/journey/journey-edit";
import JourneyListPage from "./pages/content/journey/journey-list";
import RecipeCreatePage from "./pages/content/recipe/recipe-create";
import RecipeEditPage from "./pages/content/recipe/recipe-edit";
import RecipeListPage from "./pages/content/recipe/recipe-list";
import StudioCreate from "./pages/content/studio-create";
import AlbumCreatePage from "./pages/content/taxonomy/album-create";
import AlbumEditPage from "./pages/content/taxonomy/album-edit";
import AlbumManager from "./pages/content/taxonomy/album-manager";
import VideoCreatePage from "./pages/content/video/video-create";
import VideoEditPage from "./pages/content/video/video-edit";
import VideoListPage from "./pages/content/video/video-list";
import WorkbookCreatePage from "./pages/content/workbook/workbook-create";
import WorkbookEditPage from "./pages/content/workbook/workbook-edit";
import WorkbookListPage from "./pages/content/workbook/workbook-list";
import CreateHoroscopePage from "./pages/create-horoscope";
import ExternalResetPassword from "./pages/external-reset-password";
import ForgotPassword from "./pages/forgot-password";

function AuthenticatedRoutes() {
  return (
    <Switch>
      <Route path="/" component={Dashboard} />
      <Route path="/users" component={Users} />
      <Route path="/affiliates" component={Affiliates} />
      <Route path="/affiliates/:id/profile" component={AffiliateProfile} />
      <Route path="/affiliates/requests" component={AffiliatesReview} />
      <Route path="/affiliates/requests/:id" component={PartnerRequestDetail} />
      <Route path="/users/:id" component={Users} />
      <Route path="/subscriptions">
        <RequirePermission category="subscriptions" action="view_dashboard">
          <Subscriptions />
        </RequirePermission>
      </Route>
      <Route path="/content" component={ContentLibrary} />
      <Route path="/user-support" component={UserSupport} />
      <Route path="/internal-users" component={InternalUsers} />
      <Route path="/coaches" component={Coaches} />
      <Route path="/coaches/:id/profile" component={CoachProfile} />
      <Route path="/profile" component={Profile} />
      <Route path="/health" component={SystemHealth} />
      <Route path="/notifications" component={Notifications} />
      <Route path="/audit" component={AuditLogs} />
      <Route path="/gdpr" component={GdprCompliance} />
      <Route path="/settings" component={Settings} />
      <Route path="/studio" component={ContentStudio} />
      <Route path="/studio/horoscope/create" component={CreateHoroscopePage} />
      <Route
        path="/studio/horoscope/edit/:id"
        component={CreateHoroscopePage}
      />
      <Route path="/studio/horoscope" component={HoroscopePage} />
      <Route path="/studio/tag-groups" component={TagGroups} />
      <Route path="/studio/categories" component={Categories} />
      <Route path="/studio/video" component={VideoListPage} />
      <Route path="/studio/video/new" component={VideoCreatePage} />
      <Route path="/studio/video/:id/edit">
        {(params: { id: string }) => <VideoEditPage id={params.id} />}
      </Route>
      <Route path="/studio/audio" component={AudioListPage} />
      <Route path="/studio/audio/new" component={AudioCreatePage} />
      <Route path="/studio/audio/version/:versionId/edit">
        {(params: { versionId: string }) => (
          <AudioEditPage versionId={params.versionId} />
        )}
      </Route>
      <Route path="/studio/audio/:id/edit">
        {(params: { id: string }) => <AudioEditPage id={params.id} />}
      </Route>
      <Route path="/studio/blogs" component={BlogListPage} />
      <Route path="/studio/blogs/new" component={BlogCreatePage} />
      <Route path="/studio/blogs/:id/edit">
        {(params: { id: string }) => <BlogEditPage id={params.id} />}
      </Route>
      <Route path="/studio/recipes" component={RecipeListPage} />
      <Route path="/studio/recipes/new" component={RecipeCreatePage} />
      <Route path="/studio/recipes/:id/edit">
        {(params: { id: string }) => <RecipeEditPage id={params.id} />}
      </Route>
      <Route path="/studio/affirmations" component={AffirmationListPage} />
      <Route
        path="/studio/affirmations/new"
        component={AffirmationCreatePage}
      />
      <Route path="/studio/affirmations/:id/edit">
        {(params: { id: string }) => <AffirmationEditPage id={params.id} />}
      </Route>

      <Route path="/studio/challenges" component={ChallengeListPage} />
      <Route path="/studio/challenges/new" component={ChallengeCreatePage} />
      <Route path="/studio/challenges/:id/edit">
        {(params: { id: string }) => <ChallengeEditPage id={params.id} />}
      </Route>
      <Route path="/studio/workbooks" component={WorkbookListPage} />
      <Route path="/studio/workbooks/new" component={WorkbookCreatePage} />
      <Route path="/studio/workbooks/:id/edit">
        {(params: { id: string }) => <WorkbookEditPage id={params.id} />}
      </Route>

      <Route path="/studio/albums" component={AlbumManager} />
      <Route path="/studio/albums/new" component={AlbumCreatePage} />
      <Route path="/studio/albums/:id/edit">
        {(params: { id: string }) => <AlbumEditPage id={params.id} />}
      </Route>

      <Route path="/studio/journeys" component={JourneyListPage} />
      <Route path="/studio/journeys/new" component={JourneyCreatePage} />
      <Route path="/studio/journeys/:id/edit">
        {(params: { id: string }) => <JourneyEditPage id={params.id} />}
      </Route>
      <Route path="/content-operations">
        {/* Same roles as the sidebar entry and the server's checkDashboardAccess. */}
        <RequireRole
          roles={["super_admin", "admin", "coach", "coach_manager"]}
          fallback={<NotFound />}
        >
          <ContentOperationsPage />
        </RequireRole>
      </Route>
      <Route path="/studio/review" component={ReviewPage} />
      <Route path="/studio/published" component={PublishedPage} />
      <Route path="/studio/quality" component={QualityPage} />
      <Route path="/studio/activity" component={ActivityPage} />
      <Route path="/studio/create" component={StudioCreate} />
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthenticatedShell() {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between gap-4 px-4 h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shrink-0">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-4">
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <AuthenticatedRoutes />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AppRouter() {
  const [location] = useLocation();

  if (location === "/login") {
    return (
      <LoginGuard>
        <Login />
      </LoginGuard>
    );
  }

  if (location.startsWith("/accept-internal-invitation")) {
    return <AcceptInternalInvitation />;
  }

  if (location.startsWith("/accept-invitation")) {
    return <AcceptInvitation />;
  }

  if (location.startsWith("/external-reset-password")) {
    return <ExternalResetPassword />;
  }

  if (location.startsWith("/forgot-password")) {
    return <ForgotPassword />;
  }

  if (location.startsWith("/account-deletion")) {
    return <AccountDeletion />;
  }

  return (
    <RequireAuth>
      <AuthenticatedShell />
    </RequireAuth>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <AppRouter />
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
